<template>
    <div class="portal-viewport">
        <div class="portal-frame">
            <slot />
        </div>
    </div>
</template>

<script>
/**
 * F4 (Service Portals roadmap): shared shell for the customer and technician
 * portals. "Mobile only" per the original request means a fixed phone-width
 * layout even when opened on a desktop browser -- achieved here purely with
 * CSS (max-width + centering), never device/UA detection.
 *
 * Also the single source of the portal design system: every color/radius/
 * shadow token a portal page needs is a CSS custom property set on
 * .portal-frame here. Custom properties cross Vue's scoped-style boundary
 * normally (only class/attribute selectors get scoped, not inherited custom
 * properties), so any page rendered via the default slot can just reference
 * var(--portal-accent) etc. without redeclaring the palette itself.
 *
 * Convention for any page rendered inside this shell: don't use Vuetify's
 * grid system (v-container/v-row/v-col) or responsive props (sm=/md=/lg=) --
 * those compute breakpoints from the real window width, not this shell's
 * width, so a v-col md="6" would still try to go two-up on an actual desktop
 * window even though it's visually trapped in a 430px box. Use plain flexbox
 * or CSS grid instead. Non-grid Vuetify components (v-btn, v-text-field,
 * v-icon, v-card, etc.) are fine.
 */
export default {
    name: 'MobilePortalShell',
}
</script>

<style scoped>
.portal-viewport {
    min-height: 100vh;
    width: 100%;
    display: flex;
    justify-content: center;
    background:
        radial-gradient(circle at 50% 0%, rgba(221, 126, 31, 0.16), transparent 55%),
        #0d151f;
}
.portal-frame {
    /* Dashboard-cluster palette: deep navy ink on a cool paper background,
       an amber gauge-light accent, and semantic states kept distinct from
       the accent hue (teal for good, warm gold for pending, brick for bad). */
    --portal-bg: #eef1f6;
    --portal-surface: #ffffff;
    --portal-surface-2: #f6f8fb;
    --portal-ink: #142433;
    --portal-ink-muted: #5c6c7c;
    --portal-border: #dfe6ee;
    --portal-accent: #dd7e1f;
    --portal-accent-dark: #b8630f;
    --portal-accent-ink: #ffffff;
    --portal-accent-soft: #fbead4;
    --portal-good: #1c7a63;
    --portal-good-soft: #e0f3ee;
    --portal-warn: #b8860b;
    --portal-warn-soft: #f8eecd;
    --portal-bad: #b0392f;
    --portal-bad-soft: #fae4e1;
    --portal-radius-lg: 18px;
    --portal-radius-md: 12px;
    --portal-radius-pill: 999px;
    --portal-shadow: 0 1px 2px rgba(20, 36, 51, .06), 0 8px 20px -6px rgba(20, 36, 51, .12);

    width: 100%;
    max-width: 430px;
    min-height: 100vh;
    background: var(--portal-bg);
    color: var(--portal-ink);
    position: relative;
    box-shadow: 0 0 60px rgba(0, 0, 0, .45);
}
</style>
